import { useEffect, useRef, useState } from "react";

type Props = { url: string; title: string; summary?: string };

type Target = { label: string; href: (url: string, title: string, summary: string) => string };

// Each network reads a different parameter, and every one of them expects the
// address already encoded.
const targets: Target[] = [
  {
    // share-offsite passes only a card, so when LinkedIn cannot read the page
    // the composer opens empty. The feed composer takes the text itself, which
    // means the title and the link are in the post either way.
    label: "LinkedIn",
    href: (url, title) =>
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${title}\n\n${url}`)}`
  },
  {
    label: "X",
    href: (url, title) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
  },
  {
    label: "Facebook",
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  },
  {
    label: "WhatsApp",
    href: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`
  },
  {
    label: "Email",
    href: (url, title, summary) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(summary ? `${summary}\n\n${url}` : url)}`
  }
];

/** Sharing needs no account, so this never asks anybody to sign in. */
export function ShareButton({ url, title, summary = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  // Phones and some desktops offer the system sheet, which reaches installed
  // apps this menu cannot. Where it exists it is the better first option.
  const native = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function shareNatively() {
    try {
      await navigator.share({ title, text: summary || title, url });
      setOpen(false);
    } catch {
      // Dismissing the sheet is not a failure; leave the menu as it was.
    }
  }

  return (
    <div className="share" ref={container}>
      <button type="button" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen((value) => !value)}>
        Share
      </button>
      {open ? (
        <div className="share-menu" role="menu">
          {native ? (
            <button type="button" role="menuitem" onClick={shareNatively}>
              Share via…
            </button>
          ) : null}
          {targets.map((target) => (
            <a
              key={target.label}
              role="menuitem"
              href={target.href(url, title, summary)}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => setOpen(false)}
            >
              {target.label}
            </a>
          ))}
          <button type="button" role="menuitem" onClick={copy}>
            {copied ? "Link copied" : "Copy link"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
